/* @flow */
import React, { useContext, useEffect, useState } from "react";
import Backbone from "backbone";
import SizeMe from "react-sizeme";
import prefixStyles from "nx-components/utils/prefixStyles";
import Theme from "nx-components/Theme";
import ThermostatSetpoints from "nx-components/Thermostat/ThermostatSetpoints";
import ScrollableArea from "nexia_mobile/components/Scrollable/ScrollableArea";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Page from "nexia_mobile/components/Page";
import AppBarWrapper from "nexia_mobile/components/TopBar/AppBarWrapper";
import CommonAppBarContainer from "nexia_mobile/components/TopBar/CommonAppBarContainer";
import Button from "nx-components/Button";
import ButtonGroup from "nx-components/ButtonGroup";
import { showConfirmationDialog } from "nexia_mobile/components/Dialogs/ConfirmationDialog";
import { showPivotDevices } from "nexia_mobile/components/Dialogs/Pivot/PivotDevicesGroupsDialog";
import { history } from "nexia_mobile/history";
import PivotDemoBanner from "nexia_mobile/components/Commercial/PivotDemoBanner";
import UserLanguageContext from "nx-components/ContextWrappers/UserLanguageContext";

type Props = {
  model: Backbone.Model,
  size: {
    width: number,
    height: number,
  },
};

type State = {
  preset_heat1?: number,
  preset_cool1?: number,
  preset_heat2?: number,
  preset_cool2?: number,
};

const OccupancySetpointsPage = (props: Props) => {
  /* eslint-disable camelcase */
  const [state: State, setState] = useState({
    preset_heat1: null,
    preset_cool1: null,
    preset_heat2: null,
    preset_cool2: null,
  });
  /* eslint-enable camelcase */

  const languageContext = useContext(UserLanguageContext);
  const language =
    languageContext.localeSet.nexia_mobile.pages.OccupancySetpointsPage;

  useEffect(() => {
    Dispatch("DISABLE_LOCATION_POLLING");
    return () => {
      Dispatch("ENABLE_LOCATION_POLLING");
    };
  }, []);

  const showSpinner = (message) => {
    Dispatch("SHOW_SPINNER", message);
  };

  const hideSpinner = () => {
    Dispatch("HIDE_SPINNER");
  };

  const handleChangesStarted = () => {
    // Is required but do nothing. Needs to be refactored out of ThermostatSetpoints.
  };

  const handleApplyChanges = () => {
    const { model } = props;
    const message = language.savingSetpoints;
    const onDone = () => {
      Dispatch("SHOW_TOAST", language.saveSuccessful);
      hideSpinner();
      history.goBack();
    };
    const onError = () => {
      Dispatch("SHOW_TOAST", language.saveError);
      hideSpinner();
    };
    const onChoose = (ids) => {
      showSpinner(message);
      model.setGroupOccupancySetpoints(ids, state).then(onDone, onError);
    };
    showConfirmationDialog({
      message: language.applyToAdditionalStats,
      cancelButtonText: language.no,
      confirmButtonText: language.yes,
      onConfirm: () => {
        showPivotDevices({
          open: true,
          targetId: model.get("id"),
          onChoose,
        });
      },
      onCancel: () => {
        showSpinner(message);
        model.setOccupancySetpoints(state).then(onDone, onError);
      },
    });
  };

  const handleChangesEnded = ({ setpointHeat, setpointCool, label }) => {
    let newSetpoints;
    /* eslint-disable camelcase */
    if (label === "Occupied") {
      newSetpoints = {
        preset_heat1: setpointHeat,
        preset_cool1: setpointCool,
      };
    } else {
      newSetpoints = {
        preset_heat2: setpointHeat,
        preset_cool2: setpointCool,
      };
    }
    /* eslint-enable camelcase */
    setState({
      ...state,
      ...newSetpoints,
    });
  };

  const handleOccupiedChangesEnded = ({ setpointHeat, setpointCool }) => {
    handleChangesEnded({
      setpointHeat,
      setpointCool,
      label: "Occupied",
    });
  };

  const handleUnoccupiedChangesEnded = ({ setpointHeat, setpointCool }) => {
    handleChangesEnded({
      setpointHeat,
      setpointCool,
      label: "Unoccupied",
    });
  };

  const handleChangesCanceled = () => {
    // Is required but do nothing. Needs to be refactored out of ThermostatSetpoints.
  };

  const { model, size } = props;
  /* eslint-disable camelcase */
  const { preset_heat1, preset_cool1, preset_heat2, preset_cool2 } = state;
  /* eslint-enable camelcase */
  const occupancy = model.get("occupancy_setpoints");

  const isPortrait = size.width < size.height;
  const styles = prefixStyles({
    contents: {
      flex: "1 1 auto",
      display: "flex",
      justifyContent: "center",
      flexDirection: isPortrait ? "column" : "row",
      alignItems: "center",
      backgroundColor: "white",
      alignSelf: "center",
      width: "14em",
      maxWidth: "80vw",
      margin: "auto",
    },
    setpoints: {
      margin: "1em",
    },
  });

  /* eslint-disable camelcase */
  return (
    <Page name="OccupancySetpoints" fullyLoaded>
      <AppBarWrapper>
        <CommonAppBarContainer title={language.title} iconRight={null} />
      </AppBarWrapper>
      <ScrollableArea>
        <div style={styles.contents}>
          <div style={styles.setpoints}>
            <ThermostatSetpoints
              occuLabel={language.occupiedLabel}
              setpointHeat={preset_heat1 || occupancy.preset_heat1}
              setpointHeatMin={model.get("setpoint_heat_min")}
              setpointHeatMax={model.get("setpoint_heat_max")}
              setpointCool={preset_cool1 || occupancy.preset_cool1}
              setpointCoolMin={model.get("setpoint_cool_min")}
              setpointCoolMax={model.get("setpoint_cool_max")}
              onChangesStarted={handleChangesStarted}
              onChangesEnded={handleOccupiedChangesEnded}
              onChangesCanceled={handleChangesCanceled}
              setpointIncrement={model.get("setpoint_increment")}
              setpointDelta={model.get("setpoint_delta")}
              changeBufferMs={0}
              isCommercial
            />
          </div>
          <div style={styles.setpoints}>
            <ThermostatSetpoints
              occuLabel={language.unoccupiedLabel}
              setpointHeat={preset_heat2 || occupancy.preset_heat2}
              setpointHeatMin={model.get("setpoint_heat_min")}
              setpointHeatMax={model.get("setpoint_heat_max")}
              setpointCool={preset_cool2 || occupancy.preset_cool2}
              setpointCoolMin={model.get("setpoint_cool_min")}
              setpointCoolMax={model.get("setpoint_cool_max")}
              onChangesStarted={handleChangesStarted}
              onChangesEnded={handleUnoccupiedChangesEnded}
              onChangesCanceled={handleChangesCanceled}
              setpointIncrement={model.get("setpoint_increment")}
              setpointDelta={model.get("setpoint_delta")}
              changeBufferMs={0}
              isCommercial
            />
          </div>
        </div>
        <div style={styles.contents}>
          <ButtonGroup>
            <Button onTap={() => history.goBack()} theme="ghost">
              {language.cancel}
            </Button>
            <Button
              //                disabled={_isEditing}
              onTap={handleApplyChanges}
            >
              {language.apply}
            </Button>
          </ButtonGroup>
        </div>
      </ScrollableArea>
      <PivotDemoBanner />
    </Page>
  );
  /* eslint-enable camelcase */
};

export default SizeMe({
  monitorHeight: true,
  monitorWidth: true,
  refreshRate: Theme.RESPONSIVE_REFRESH_RATE,
})(OccupancySetpointsPage);



// WEBPACK FOOTER //
// ./src/components/Pages/OccupancySetpointsPage.jsx